import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import SOAddUpdateProduct from "../AddUpdateProduct/SOAddUpdateProduct";
import { useSearchParams } from "react-router-dom";
import { truncateString } from "utils/UtilityFuncs";

export default function AddFromPreviousWorkOrder({
  selectedWO,
  refreshFunc,
  sortId,
  showModal,
  setShowEditModalAfterImport,
}) {
  const dispatch = useDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  /*   All States
   ********************************************* */
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalSearchResult, setTotalSearchResult] = useState(null);

  const [showProductsModal, setShowProductsModal] = useState(false);

  /*   All Functions
   ********************************************* */
  function loadingOn() {
    setLoading(true);
  }
  function loadingOff() {
    setLoading(false);
  }

  const clearSearch = () => {
    setSearchText("");
    setSearchResults(null);
  };

  // Search function with pagination (using shipment endpoint)
  const handleSearch = async (opts = {}) => {
    try {
      loadingOn();
      const pageToUse = opts.page || 1;
      let body = {
        query: searchText,
        page: pageToUse,
        limit,
        searchFields: ["woId", "customerName", "jobName"],
      };
      const { data } = await API.post("/work-orders-search", body, {
        headers: authHeader(),
      });
      if (data?.success) {
        setSearchResults(data?.workOrders || []);
        setTotalSearchResult(data?.total || 0);
        setPage(pageToUse);
      }
      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  // Load more function (using shipment endpoint)
  const handleLoadMore = async () => {
    try {
      loadingOn();
      const nextPage = page + 1;
      let body = {
        query: searchText,
        page: nextPage,
        limit,
        searchFields: ["woId", "customerName", "jobName"],
      };
      const { data } = await API.post("/work-orders-search", body, {
        headers: authHeader(),
      });
      if (data?.success) {
        setSearchResults((prev) => [...prev, ...(data?.workOrders || [])]);
        setTotalSearchResult(data?.total || 0);
        setPage(nextPage);
      }
      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const addProduct = async (product) => {
    if (!product) {
      return alert("Please select a valid product");
    }
    let copyObject = null;
    let updatedProductImage = product?.productImage
      ? product?.productImage?.replace(selectedItem?.id, selectedWO?.id)
      : null;
    if (product?.productImage) {
      copyObject = {
        oldKey: product?.productImage,
        newKey: updatedProductImage,
      };
    }

    try {
      loadingOn();

      delete product.sortId;
      delete product.id;
      delete product.createdAt;
      delete product.updatedAt;
      delete product.WOId;
      delete product.productSizes;
      delete product?.approveStatus;
      delete product?.approvedTime;
      delete product?.approvalComments;

      product.totalPrice = "00.00";
      product.totalQuantity = "0.00";

      product.isReady = false;
      product.purchased = false;
      product.received = false;

      // product.productSizes = handleRemovePriceFromSizes(product.productSizes);

      let productsData = {
        ...product,
        WOId: selectedWO?.id,
        sortId: sortId,
        productImage: updatedProductImage,
      };

      const resp = await API.post(
        `/wo-products`,
        {
          data: productsData,
          copyObject,
        },
        { headers: authHeader() },
      );
      if (resp.status === 200) {
        await refreshFunc();
        loadingOff();
        setShowEditModalAfterImport(resp?.data);
        showModal(false);
      } else {
        throw new Error(resp.message);
      }
    } catch (err) {
      console.log(err.message);
      loadingOff();
    }
  };

  const handleProductOpen = async (item) => {
    try {
      loadingOn();
      const { data } = await API.get("/wo-products/" + item.id, {
        headers: authHeader(),
      });
      if (data?.success) {
        setAllProducts(data?.data);
        setUpdateId(item.id);
        setSelectedItem(item);
        setShowProductsModal(true);
      }

      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const setUpdateId = (id) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("productId", id);
      return newParams.toString();
    });
  };

  const removeUpdateId = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("productId");
      return newParams.toString();
    });
  };

  /*   All UseEffects
   ********************************************* */

  return (
    <div className="bg-white custom-border-radius custom-shadow p-3">
      {loading && <Spinner />}
      <div className="mb-3">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h3 className="mb-0">Select Products From Previous Work Order</h3>
            <small className="text-muted">
              Search another WO to reuse its products
            </small>
          </div>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch({ page: 1 });
        }}
        className="mb-3"
      >
        <label className="form-label fw-semibold">Search WOs</label>
        <div className="input-group">
          <span className="input-group-text bg-white">
            <i className="mdi mdi-magnify"></i>
          </span>
          <input
            value={searchText}
            required
            type="search"
            className="form-control"
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Enter WO id, job name, or customer"
          />
          {searchText && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={clearSearch}
            >
              Clear
            </button>
          )}
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Searching..." : "Search"}
          </button>
        </div>
        <div className="form-text">
          Tip: try part of a job name or customer to broaden results
        </div>
      </form>
      {/* Results per page selector */}
      {searchResults && (
        <div className="d-flex align-items-center gap-3 mb-2">
          <label
            className="form-label text-nowrap mb-0 fw-bold"
            style={{ fontSize: "1rem" }}
          >
            Results per page
          </label>
          <select
            className="form-select form-select-sm w-auto"
            value={limit}
            onChange={(e) => {
              setLimit(parseInt(e.target.value));
              setPage(1);
              handleSearch({ page: 1 });
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          {searchResults.length > 0 && (
            <span className="text-muted small">
              Showing <b>{searchResults.length}</b> of{" "}
              <b>{totalSearchResult}</b>
            </span>
          )}
        </div>
      )}
      {searchResults && (
        <>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-2">Search Results</h5>
            <span className="text-muted small">
              {totalSearchResult || 0} found
            </span>
          </div>
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th className="fw-bold">WO</th>
                <th className="fw-bold">Job Name</th>
                <th className="fw-bold">Customer</th>
                <th className="fw-bold text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {searchResults?.map((item) => (
                <tr key={item.id} className="">
                  <td className="py-2">
                    <span className="badge bg-light text-dark border">
                      {item?.woId}
                    </span>
                  </td>
                  <td className="py-1">{truncateString(item?.jobName, 30)}</td>
                  <td className="py-1">
                    {truncateString(item?.customerName, 30)}
                  </td>
                  <td className="py-1 text-end">
                    <button
                      onClick={() => {
                        handleProductOpen(item);
                      }}
                      className="btn btn-sm btn-outline-primary"
                    >
                      <i className="mdi mdi-eye"></i> Products
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalSearchResult > searchResults.length && (
            <div className="p-2 text-center bg-light">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={handleLoadMore}
                disabled={loading}
              >
                Load More
              </button>
            </div>
          )}
          {searchResults?.length === 0 && (
            <div className="alert alert-info py-2">
              No results. Try a different search.
            </div>
          )}
        </>
      )}
      {/*   SelectProduct Modal
       ********************************************* */}
      <Modal
        size="xl"
        show={showProductsModal}
        onHide={() => {
          setShowProductsModal(false);
          removeUpdateId();
        }}
        contentClassName="bg-transparent"
      >
        <div className="bg-light custom-border-radius custom-shadow-sm p-3">
          {loading && <Spinner />}
          {selectedItem && (
            <SOAddUpdateProduct
              showModal={setShowProductsModal}
              allProducts={allProducts}
              selectedWO={selectedItem}
              refreshFunc={() => {}}
              setUpdateId={setUpdateId}
              removeUpdateId={removeUpdateId}
              lastSortId={0}
              selectMode={true}
              onSelectProduct={addProduct}
            />
          )}
        </div>
      </Modal>
    </div>
  );
}
